import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  PiMatrixAgentThinkingLevel,
  PiStructuredGenerationExecution,
  PiStructuredGenerationRequest,
} from '../types/piMatrixAgent'
import type {
  ChapterStructuralRangeLayer,
  StructuralRangeKind,
} from '../types/structuralRangeTypes'
import { getCollationRowPrimaryTitle } from './ledgerSafeRowTitles'
import { sha256Hex } from './sha256'

export const STRUCTURAL_RANGE_TITLE_SUGGESTION_SCHEMA = 'variorum.structural-range-title-suggestion.v1'
export const STRUCTURAL_RANGE_GENERATION_RUN_SCHEMA_VERSION = 2 as const
export const STRUCTURAL_RANGE_GENERATION_MAX_ROWS = 80
export const STRUCTURAL_RANGE_GENERATION_STORAGE_PREFIX = 'ledgerSafe.structuralRangeTitleGeneration.v1'
export const STRUCTURAL_RANGE_GENERATION_FAILURE_SCHEMA_VERSION = 1 as const
export const STRUCTURAL_RANGE_GENERATION_FAILURE_STORAGE_PREFIX = 'ledgerSafe.structuralRangeTitleGenerationFailure.v1'

const MAX_READING_CHARACTERS = 800
const MAX_WITNESSES_PER_ROW = 8

export interface StructuralRangeTitleTarget {
  rangeId: string | null
  title: string
  kind: StructuralRangeKind | ''
  startRowId: string
  endRowId: string
  parentRangeId: string
}

export interface StructuralRangeTitleSuggestion {
  title: string
  confidence: number | null
  provenance: {
    sourceKind: 'pi-structured-generation'
    generationId: string
    provider: string
    modelId: string
  }
}

export interface LedgerSafeStructuralRangeGenerationRun {
  schemaVersion: typeof STRUCTURAL_RANGE_GENERATION_RUN_SCHEMA_VERSION
  id: string
  chapterId: string
  rowIds: string[]
  inputFingerprint: string
  generationId: string
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
  target: StructuralRangeTitleTarget
  suggestion: StructuralRangeTitleSuggestion
  status: 'ready' | 'used' | 'dismissed'
  createdAt: string
  updatedAt: string
  usageTotal: number
  elapsedMs: number
}

export interface LedgerSafeStructuralRangeGenerationFailure {
  schemaVersion: typeof STRUCTURAL_RANGE_GENERATION_FAILURE_SCHEMA_VERSION
  chapterId: string
  rowIds: string[]
  inputFingerprint: string
  errorMessage: string
  execution: PiStructuredGenerationExecution
  failedAt: string
}

const clean = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim()

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const generateId = (prefix: string): string => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `${prefix}:${crypto.randomUUID()}`
    : `${prefix}:${Date.now()}:${Math.random().toString(16).slice(2)}`
)

const stableRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => (
  left.position - right.position || left.labelNumber - right.labelNumber || left.id.localeCompare(right.id)
))

const promptRow = (row: AnchoredRow) => ({
  row_id: row.id,
  label: `${row.labelPrefix}-${String(row.labelNumber).padStart(3, '0')}`,
  row_title: getCollationRowPrimaryTitle(row) || null,
  readings: row.unitMappings
    .filter(mapping => !mapping.excluded && clean(mapping.textSnapshot))
    .slice(0, MAX_WITNESSES_PER_ROW)
    .map(mapping => ({
      witness_id: mapping.witnessId,
      text: clean(mapping.textSnapshot).slice(0, MAX_READING_CHARACTERS),
    })),
})

export const fingerprintStructuralRangeGenerationInput = ({
  rows,
  layer,
  target,
}: {
  rows: AnchoredRow[]
  layer: ChapterStructuralRangeLayer
  target: StructuralRangeTitleTarget
}): string => sha256Hex(JSON.stringify({
  rows: stableRows(rows).map(promptRow),
  layerVersion: layer.version,
  target,
  confirmedRanges: layer.ranges
    .filter(range => range.status === 'confirmed')
    .map(range => ({
      id: range.id,
      title: range.title,
      kind: range.kind,
      startRowId: range.startRowId,
      endRowId: range.endRowId,
      parentRangeId: range.parentRangeId,
    })),
}))

export const buildStructuralRangeStructuredGenerationRequest = ({
  rows,
  layer,
  target,
  modelId,
  thinkingLevel,
}: {
  rows: AnchoredRow[]
  layer: ChapterStructuralRangeLayer
  target: StructuralRangeTitleTarget
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
}): PiStructuredGenerationRequest => {
  const orderedRows = stableRows(rows)
  if (!orderedRows.length) throw new Error('Choose a row range to analyze')
  if (orderedRows.length > STRUCTURAL_RANGE_GENERATION_MAX_ROWS) {
    throw new Error(`Automatic structure analysis is limited to ${STRUCTURAL_RANGE_GENERATION_MAX_ROWS} selected rows`)
  }
  const rowIds = orderedRows.map(row => row.id)
  if (target.startRowId !== rowIds[0] || target.endRowId !== rowIds[rowIds.length - 1]) {
    throw new Error('The title target must match the exact selected row range')
  }
  return {
    schemaVersion: 'variorum.pi-structured-generation.v1',
    model: {
      provider: 'openai-codex',
      modelId,
    },
    thinkingLevel,
    systemPrompt: [
      'You write one concise scholarly English title for one exact editorial range in a manuscript collation.',
      'Return JSON only and obey the response schema exactly.',
      `Return one object with schema_version equal to ${STRUCTURAL_RANGE_TITLE_SUGGESTION_SCHEMA}, one title, and confidence.`,
      'Title the supplied target_range as a whole.',
      'Do not discover, propose, classify, subdivide, or title internal ranges.',
      'Use the supplied rows and confirmed ranges only as context for the target title.',
      'Describe the principal narrative or argumentative action in 3 to 12 words.',
      'Do not mention witness identifiers, row labels, manuscript variation, or uncertainty in the title.',
      'Confidence may be null.',
    ].join(' '),
    prompt: JSON.stringify({
      schema_version: STRUCTURAL_RANGE_TITLE_SUGGESTION_SCHEMA,
      selected_row_ids: rowIds,
      target_range: {
        id: target.rangeId,
        current_title: clean(target.title) || null,
        kind: target.kind || null,
        start_row_id: target.startRowId,
        end_row_id: target.endRowId,
        parent_range_id: target.parentRangeId || null,
      },
      rows: orderedRows.map(promptRow),
      confirmed_range_context: layer.ranges
        .filter(range => range.status === 'confirmed')
        .map(range => ({
          id: range.id,
          title: range.title,
          kind: range.kind,
          start_row_id: range.startRowId,
          end_row_id: range.endRowId,
          parent_range_id: range.parentRangeId,
        })),
    }),
    responseSchema: {
      type: 'object',
      properties: {
        schema_version: { const: STRUCTURAL_RANGE_TITLE_SUGGESTION_SCHEMA },
        title: { type: 'string' },
        confidence: {
          anyOf: [
            { type: 'number' },
            { type: 'null' },
          ],
        },
      },
      required: ['schema_version', 'title', 'confidence'],
      additionalProperties: false,
    },
    maxResponseCharacters: 4_000,
  }
}

export const parseStructuralRangeGenerationExecution = ({
  execution,
}: {
  execution: PiStructuredGenerationExecution
}): StructuralRangeTitleSuggestion => {
  if (!execution.reply.schemaValid) {
    throw new Error(execution.reply.schemaError || 'Pi returned a range title that did not match the schema')
  }
  const payload = execution.reply.parsedJson
  if (!isRecord(payload) || payload.schema_version !== STRUCTURAL_RANGE_TITLE_SUGGESTION_SCHEMA) {
    throw new Error('Pi returned an invalid structural-range title payload')
  }
  const title = clean(payload.title)
  if (!title || title.length > 500) throw new Error('Pi returned an invalid structural-range title')
  return {
    title,
    confidence: typeof payload.confidence === 'number' && Number.isFinite(payload.confidence)
      ? Math.max(0, Math.min(1, payload.confidence))
      : null,
    provenance: {
      sourceKind: 'pi-structured-generation',
      generationId: execution.generationId,
      provider: execution.model.provider,
      modelId: execution.model.modelId,
    },
  }
}

export const createLedgerSafeStructuralRangeGenerationRun = ({
  chapterId,
  rows,
  layer,
  target,
  execution,
  suggestion,
  now = new Date().toISOString(),
}: {
  chapterId: string
  rows: AnchoredRow[]
  layer: ChapterStructuralRangeLayer
  target: StructuralRangeTitleTarget
  execution: PiStructuredGenerationExecution
  suggestion: StructuralRangeTitleSuggestion
  now?: string
}): LedgerSafeStructuralRangeGenerationRun => ({
  schemaVersion: STRUCTURAL_RANGE_GENERATION_RUN_SCHEMA_VERSION,
  id: generateId('structural-range-run'),
  chapterId,
  rowIds: stableRows(rows).map(row => row.id),
  inputFingerprint: fingerprintStructuralRangeGenerationInput({ rows, layer, target }),
  generationId: execution.generationId,
  modelId: execution.model.modelId,
  thinkingLevel: execution.thinkingLevel,
  target,
  suggestion,
  status: 'ready',
  createdAt: now,
  updatedAt: now,
  usageTotal: Number(execution.usage?.total) || 0,
  elapsedMs: Number(execution.timing?.elapsedMs) || 0,
})

export const createLedgerSafeStructuralRangeGenerationFailure = ({
  chapterId,
  rows,
  layer,
  target,
  execution,
  error,
  now = new Date().toISOString(),
}: {
  chapterId: string
  rows: AnchoredRow[]
  layer: ChapterStructuralRangeLayer
  target: StructuralRangeTitleTarget
  execution: PiStructuredGenerationExecution
  error: unknown
  now?: string
}): LedgerSafeStructuralRangeGenerationFailure => ({
  schemaVersion: STRUCTURAL_RANGE_GENERATION_FAILURE_SCHEMA_VERSION,
  chapterId,
  rowIds: stableRows(rows).map(row => row.id),
  inputFingerprint: fingerprintStructuralRangeGenerationInput({ rows, layer, target }),
  errorMessage: error instanceof Error ? error.message : String(error || 'Structural analysis failed'),
  execution,
  failedAt: now,
})

export const describeStructuralRangeGenerationFailure = (
  failure: LedgerSafeStructuralRangeGenerationFailure,
): string => {
  if (failure.execution.reply.schemaValid) {
    return `Pi returned a range title that could not be prepared: ${clean(failure.errorMessage)}. No title was used. The failed output was retained locally for diagnosis.`
  }
  const attemptCount = failure.execution.reply.attempts?.length || 1
  const validationDetail = clean(failure.execution.reply.schemaError || failure.errorMessage)
  const retryDetail = attemptCount > 1 ? ' after one automatic format-repair attempt' : ''
  return `Pi returned a range title that did not match the required format${retryDetail}: ${validationDetail}. No title was used. The failed output was retained locally for diagnosis.`
}

export const updateLedgerSafeStructuralRangeGenerationRunStatus = (
  run: LedgerSafeStructuralRangeGenerationRun,
  status: LedgerSafeStructuralRangeGenerationRun['status'],
  now = new Date().toISOString(),
): LedgerSafeStructuralRangeGenerationRun => ({ ...run, status, updatedAt: now })

const storageKey = (chapterId: string): string => `${STRUCTURAL_RANGE_GENERATION_STORAGE_PREFIX}:${chapterId}`
const failureStorageKey = (chapterId: string): string => `${STRUCTURAL_RANGE_GENERATION_FAILURE_STORAGE_PREFIX}:${chapterId}`

export const saveLedgerSafeStructuralRangeGenerationRun = (
  run: LedgerSafeStructuralRangeGenerationRun,
  storage: Pick<Storage, 'setItem'> = window.localStorage,
): void => {
  storage.setItem(storageKey(run.chapterId), JSON.stringify(run))
}

export const loadLedgerSafeStructuralRangeGenerationRun = (
  chapterId: string,
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): LedgerSafeStructuralRangeGenerationRun | null => {
  const raw = storage.getItem(storageKey(chapterId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as LedgerSafeStructuralRangeGenerationRun
    if (
      parsed.schemaVersion !== STRUCTURAL_RANGE_GENERATION_RUN_SCHEMA_VERSION
      || parsed.chapterId !== chapterId
      || !Array.isArray(parsed.rowIds)
      || !parsed.target
      || !parsed.suggestion
      || typeof parsed.suggestion.title !== 'string'
    ) return null
    return parsed
  } catch {
    return null
  }
}

export const clearLedgerSafeStructuralRangeGenerationRun = (
  chapterId: string,
  storage: Pick<Storage, 'removeItem'> = window.localStorage,
): void => {
  storage.removeItem(storageKey(chapterId))
}

export const saveLedgerSafeStructuralRangeGenerationFailure = (
  failure: LedgerSafeStructuralRangeGenerationFailure,
  storage: Pick<Storage, 'setItem'> = window.localStorage,
): void => {
  storage.setItem(failureStorageKey(failure.chapterId), JSON.stringify(failure))
}

export const loadLedgerSafeStructuralRangeGenerationFailure = (
  chapterId: string,
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): LedgerSafeStructuralRangeGenerationFailure | null => {
  const raw = storage.getItem(failureStorageKey(chapterId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as LedgerSafeStructuralRangeGenerationFailure
    if (
      parsed.schemaVersion !== STRUCTURAL_RANGE_GENERATION_FAILURE_SCHEMA_VERSION
      || parsed.chapterId !== chapterId
      || !Array.isArray(parsed.rowIds)
      || !parsed.execution?.reply
    ) return null
    return parsed
  } catch {
    return null
  }
}
